/** Цвет категории в пироге и легенде.
 *
 * ЗАЧЕМ ПО РАНГУ, А НЕ ПО ИМЕНИ. Бренд-бук требует «цвет привязан к категории
 * навсегда», и это правильное правило — но восемь оттенков на четырнадцать
 * категорий его не выдерживают: фиксированная карта неизбежно даёт двум долькам
 * один цвет, и пирог начинает врать. Синий занят интерактивом, зелёный —
 * доходом, так что расширить палитру некуда.
 *
 * Поэтому шесть самых крупных категорий получают шесть максимально разведённых
 * оттенков по убыванию суммы, а хвост сливается в серое «Остальное». Дольки
 * никогда не совпадают, а узнавание держит подпись рядом — в легенде всегда
 * имя и сумма, а не один только кружок.
 *
 * ШОВ ЗАКРЫТ (миграция 0014). У категории появился свой цвет: выбранный —
 * держится за ней навсегда, как и требует бренд-бук, и не зависит от сумм.
 * Раскладка по рангу осталась для тех, у кого цвет не выбран, — то есть по
 * умолчанию для всех, кто ничего не настраивал.
 */
import type { CategorySlice } from "./api.ts";
import { plural } from "./plural.ts";

/** Шесть оттенков в порядке максимальной разведённости соседей. */
const RANKED = ["--cat-1", "--cat-3", "--cat-2", "--cat-7", "--cat-4", "--cat-6"];
const REST = "--cat-rest";

/** Сколько долек показываем цветом. Дальше глаз всё равно не различает. */
export const TOP = 6;

/** Подпись склейки.
 *
 *  НЕ «Остальное». В справочнике живёт настоящая категория «прочее», и рядом
 *  в легенде оказывались два серых пункта с одинаковым смыслом — различить их
 *  по экрану было нечем. Число в подписи сразу говорит, что это не категория,
 *  а сколько-то категорий, и приглашает открыть состав.
 */
export function restName(count: number): string {
  return `Ещё ${plural(count, ["категория", "категории", "категорий"])}`;
}

export type Colored = CategorySlice & {
  color: string;
  /** Что именно склеено. Есть только у хвоста — по нему его и узнают. */
  members?: Colored[];
};

/** Хвост это или обычная долька. */
export function isRest(slice: Colored): boolean {
  return (slice.members?.length ?? 0) > 0;
}

/** Цвет по позиции в списке. Нужен там, где ранга по сумме нет — например на
 *  экране управления категориями: одинаково серые иконки не различишь. */
export function nthColor(i: number): string {
  return `var(${RANKED[i % RANKED.length]})`;
}

/** Топ-N цветными, хвост — одной серой долькой. Порядок сохраняется. */
export function colorize(slices: CategorySlice[]): Colored[] {
  const head = slices.slice(0, TOP).map((slice, i) => ({
    ...slice,
    // Выбранный цвет побеждает ранг. Иначе настройка ничего не значила бы:
    // человек назначил «еде» бирюзовый, а пирог перекрасил её по сумме.
    color: `var(${slice.color || RANKED[i]})`,
  }));
  const tail = slices.slice(TOP);
  if (tail.length === 0) return head;
  const grey = (s: CategorySlice): Colored => ({ ...s, color: `var(${s.color || REST})` });
  // Одна категория в хвосте — это она сама, а не «ещё одна». Показываем именем
  // и оставляем обычным фильтром: склеивать нечего, а «Ещё 1 категория» просит
  // лишнее касание ради одной строки.
  if (tail.length === 1) return [...head, grey(tail[0]!)];
  return [
    ...head,
    {
      name: restName(tail.length),
      amount: sum(tail.map((s) => s.amount)),
      share: tail.reduce((acc, s) => acc + s.share, 0),
      color: `var(${REST})`,
      // Своего id у склейки нет — фильтровать ленту по ней нечем. Зато состав
      // лежит здесь же: нажатие открывает его списком, и уже оттуда можно
      // провалиться в настоящую категорию.
      icon: null,
      category_id: null,
      members: tail.map(grey),
    },
  ];
}

/** Сложение сумм-строк через целые копейки: float здесь съел бы копейку. */
function sum(amounts: string[]): string {
  let cents = 0n;
  for (const amount of amounts) {
    const [whole = "0", frac = ""] = amount.split(".");
    cents += BigInt(whole) * 100n + BigInt((frac + "00").slice(0, 2));
  }
  const rest = cents % 100n;
  return rest === 0n
    ? String(cents / 100n)
    : `${cents / 100n}.${String(rest).padStart(2, "0")}`;
}
